import useSocketReconnect from '../../utils/useSocketReconnect';
import React, { useState, useEffect, useRef } from 'react';
import './MerxandatgeConfig.css';

function MerxandatgeConfig({ socket }) {
    useSocketReconnect(socket, () => {
        if (loading) socket.emit('.get_merxandatge_payment_info');
    });

  const [paymentSettings, setPaymentSettings] = useState(() => {
    // Load cached settings from localStorage
    try {
      const cached = localStorage.getItem('merxandatge_config_cache');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Use cache if less than 5 minutes old
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          return data;
        }
      }
    } catch (e) {
      // Ignore cache errors
    }
    return { iban: '', concept: '', commission_mode: 'user_pays' };
  });
  const [loading, setLoading] = useState(() => {
    // Not loading if we have cached data
    try {
      const cached = localStorage.getItem('merxandatge_config_cache');
      if (cached) {
        const { timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          return false;
        }
      }
    } catch (e) {
      // Ignore cache errors
    }
    return true;
  });
  const debounceRef = useRef(null);

  useEffect(() => {
    socket.emit('.get_merxandatge_payment_info');

    const handlePaymentInfo = (data) => {
      setPaymentSettings(data);
      setLoading(false);
      // Cache settings data
      try {
        localStorage.setItem('merxandatge_config_cache', JSON.stringify({
          data,
          timestamp: Date.now()
        }));
      } catch (e) {
        // Ignore cache errors
      }
    };

    socket.on('.merxandatge_payment_info', handlePaymentInfo);

    return () => {
      socket.off('.merxandatge_payment_info', handlePaymentInfo);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [socket]);

  const handlePaymentSettingChange = (field, value) => {
    const newSettings = { ...paymentSettings, [field]: value };
    setPaymentSettings(newSettings);

    // Update cache immediately for optimistic UI
    try {
      localStorage.setItem('merxandatge_config_cache', JSON.stringify({
        data: newSettings,
        timestamp: Date.now()
      }));
    } catch (e) {
      // Ignore cache errors
    }

    // Debounced autosave
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      socket.emit('.update_merxandatge_payment_info', newSettings);
    }, 500);
  };

  if (loading) {
    return (
      <div className="merxandatge-config-container">
        <div className="merxandatge-config-section">
          <h2>Configuració del pagament</h2>
          <div className="merxandatge-config-form">
            <div className="merxandatge-config-form-group">
              <label>IBAN:</label>
              <div className="merxandatge-config-skeleton-input" />
            </div>
            <div className="merxandatge-config-form-group">
              <label>Concepte:</label>
              <div className="merxandatge-config-skeleton-input" />
              <div className="merxandatge-config-skeleton-help" />
            </div>
            <div className="merxandatge-config-form-group">
              <label>Comissió de targeta:</label>
              <div className="merxandatge-config-radio-group">
                <div className="merxandatge-config-skeleton-radio" />
                <div className="merxandatge-config-skeleton-radio" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="merxandatge-config-container">
      <div className="merxandatge-config-section">
        <h2>Configuració del pagament</h2>
        
        <div className="merxandatge-config-form">
          <div className="merxandatge-config-form-group">
            <label htmlFor="config-iban">IBAN:</label>
            <input
              id="config-iban"
              type="text"
              value={paymentSettings.iban || ''}
              onChange={(e) => handlePaymentSettingChange('iban', e.target.value)}
              placeholder="ES00 0000 0000 0000 0000 0000"
            />
          </div>

          <div className="merxandatge-config-form-group">
            <label htmlFor="config-concept">Concepte:</label>
            <input
              id="config-concept"
              type="text"
              value={paymentSettings.concept || ''}
              onChange={(e) => handlePaymentSettingChange('concept', e.target.value)}
              placeholder="Comanda {nom} {cognom} {mote}"
            />
            <div className="merxandatge-config-help">
              <p>Variables disponibles: <code>{'{nom}'}</code>, <code>{'{cognom}'}</code>, <code>{'{mote}'}</code></p>
              <p>Exemple: "Comanda Marxandatge {'{nom}'} {'{cognom}'}"</p>
            </div>
          </div>

          <div className="merxandatge-config-form-group">
            <label>Comissió de targeta:</label>
            <div className="merxandatge-config-radio-group">
              <div 
                className={`merxandatge-config-radio-option ${paymentSettings.commission_mode !== 'colla_absorbs' ? 'selected' : ''}`}
                onClick={() => handlePaymentSettingChange('commission_mode', 'user_pays')}
              >
                <input
                  type="radio"
                  name="commission_mode"
                  value="user_pays"
                  checked={paymentSettings.commission_mode !== 'colla_absorbs'}
                  onChange={() => {}}
                />
                <div className="merxandatge-config-radio-content">
                  <div className="merxandatge-config-radio-title">💳 L'usuari paga la comissió</div>
                  <div className="merxandatge-config-radio-desc">La colla rep l'import complet dels productes</div>
                </div>
              </div>

              <div 
                className={`merxandatge-config-radio-option ${paymentSettings.commission_mode === 'colla_absorbs' ? 'selected' : ''}`}
                onClick={() => handlePaymentSettingChange('commission_mode', 'colla_absorbs')}
              >
                <input
                  type="radio"
                  name="commission_mode"
                  value="colla_absorbs"
                  checked={paymentSettings.commission_mode === 'colla_absorbs'}
                  onChange={() => {}}
                />
                <div className="merxandatge-config-radio-content">
                  <div className="merxandatge-config-radio-title">🏠 La colla absorbeix la comissió</div>
                  <div className="merxandatge-config-radio-desc">L'usuari paga l'import exacte dels productes</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MerxandatgeConfig;
